<template>
  <div :class="{ [$style.container]: inCard }">
    <table :class="$style.table">
      <TableHeader
        :columns="columns"
        :renderActionColumn="renderActionColumn"
      />
      <TableBody
        :columns="columns"
        :inContainer="inContainer"
        :renderActionColumn="renderActionColumn"
        :tableData="tableData"
      >
        <template
          v-slot:expandedContent="props"
        >
          <slot
            :columns="props.columns"
            name="expandedContent"
            :record="props.record"
          />
        </template>
      </TableBody>
    </table>
    <div :class="$style.controlsContainer">
      <div :class="$style.rowsPerPageContainer">
        Rows per page:
        <DropDownMenu
          :class="$style.rowsPerPageDropDown"
          :displayMenuListOnTop="showRowsPerPageMenuOnTop"
          :isLargeDropDown="false"
          :leftAlignList="true"
          :menuSections="[{ menuItems: rowsPerPageOptions }]"
          :menuTitle="String(rowsPerPage)"
          menuFontSize="small"
          :useButtonToActivate="false"
        />
      </div>
      <div :class="$style.paginationAndNumResultsContainer">
        <div
          v-show="totalCount > rowsPerPage"
          :class="$style.paginationContainer"
        >
          <button
            v-for="(pageNumButton, index) in pageNumButtonsInfo"
            :key="index"
            :class="[$style.paginationButton, {
              [$style.paginationButtonActive]: currentPageNum + 1 === pageNumButton.label,
              [$style.paginationButtonClickable]: pageNumButton.label !== '...',
            }]"
            :disabled="pageNumButton.disabled"
            @click="pageNumButton.action"
          >
            {{ pageNumButton.label }}
          </button>
        </div>
        <div :class="$style.numResultsShowingText">
          Showing
          <strong>
            {{ (currentPageNum * rowsPerPage) + 1 }}
            -
            {{ Math.min((currentPageNum + 1) * rowsPerPage, totalCount) }}
          </strong>
          of
          <strong>{{ totalCount }}</strong>
          <span>{{ footerDetails }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { TableWithPaginationConsts } from 'client/util/Constants';
import DropDownMenu from 'components/DropDownMenu/DropDownMenu.vue';
import TableBody from 'components/Tables/TableBody.vue';
import TableHeader from 'components/Tables/TableHeader.vue';
import TableMixin from 'components/Tables/TableMixin';

export default {
  name: 'TableWithPagination',

  components: {
    DropDownMenu,
    TableBody,
    TableHeader,
  },

  mixins: [
    TableMixin,
  ],

  props: {
    currentPageNum: {
      type: Number,
      default: 0,
    },
    footerDetails: {
      type: String,
      default: '',
    },
    inCard: {
      type: Boolean,
      default: true,
    },
    rowsPerPage: {
      type: Number,
      default: 10,
      validator: value => TableWithPaginationConsts.rowsPerPageOptions.includes(value),
    },
    showRowsPerPageMenuOnTop: Boolean,
    totalCount: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    pageNumButtonsInfo() {
      const totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      const pageStartNum = Math.max(0, this.currentPageNum - 2);
      const pageEndNum = Math.min(totalPages - 1, this.currentPageNum + 2);

      const pageNums = [];
      if (pageStartNum > 0) pageNums.push(0); // always show first page
      if (pageStartNum > 1) pageNums.push(null); // null represents '...'
      for (let i = pageStartNum; i <= pageEndNum; i += 1) {
        pageNums.push(i);
      }
      if (pageEndNum < totalPages - 2) pageNums.push(null); // null represents '...'
      if (pageEndNum < totalPages - 1) pageNums.push(totalPages - 1); // always show last page
      const pageNumButtonList = pageNums.map((pageNum) => {
        if (pageNum === null) {
          return {
            action: () => {},
            disabled: false,
            label: '...',
          };
        }
        return {
          action: () => this.navigateToPageNum(pageNum),
          disabled: false,
          label: pageNum + 1,
        };
      });

      return [
        {
          action: this.navigateToPreviousPage,
          disabled: this.currentPageNum === 0,
          label: '<',
        },
        ...pageNumButtonList,
        {
          action: this.navigateToNextPage,
          disabled: (this.currentPageNum + 1) >= totalPages,
          label: '>',
        },
      ];
    },

    rowsPerPageOptions() {
      return TableWithPaginationConsts.rowsPerPageOptions.map(rowsPerPage => ({
        action: () => this.modifyRowsPerPage(rowsPerPage),
        label: rowsPerPage,
      }));
    },
  },

  methods: {
    modifyRowsPerPage(rowsPerPage) {
      this.$emit('pageChange', {
        currentPageNum: 0,
        rowsPerPage,
      });
    },

    navigateToNextPage() {
      this.$emit('pageChange', {
        currentPageNum: this.currentPageNum + 1,
        rowsPerPage: this.rowsPerPage,
      });
    },

    navigateToPageNum(pageNum) {
      this.$emit('pageChange', {
        currentPageNum: pageNum,
        rowsPerPage: this.rowsPerPage,
      });
    },

    navigateToPreviousPage() {
      this.$emit('pageChange', {
        currentPageNum: this.currentPageNum - 1,
        rowsPerPage: this.rowsPerPage,
      });
    },
  },
};
</script>

<style scoped module>
@import './css/tableStyles.css';

@value (gradientDark4, gradientLight3, gradientLight4) from '~css/colors.css';

.controlsContainer {
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  padding-top: 12px;
}

.expandButton {
  background: white;
  padding: 0 0 0 10px;
}

.rowsPerPageContainer { white-space: nowrap; }

.rowsPerPageDropDown {
  background-color: gradientLight3;
  display: inline-flex;
  font-weight: 600;
  margin-left: 10px;
  padding: 4px 10px;
  position: relative;
  top: -2px;
}

.paginationAndNumResultsContainer {
  align-items: center;
  display: flex;
  flex-direction: row-reverse;
  flex-wrap: wrap;
  margin-bottom: -12px;
}

.paginationContainer,
.numResultsShowingText {
  margin-bottom: 12px;
  margin-left: 15px;
  text-align: right;
}

.numResultsShowingText { white-space: nowrap; }

.paginationButton {
  cursor: default;
  font-weight: 600;
  padding: 4px 0;
  text-align: center;
  width: 24px;
}

.paginationButtonClickable {
  background-color: white;
  cursor: pointer;
}

.paginationButton.paginationButtonActive { background-color: gradientLight4; }
.paginationButtonClickable:hover { background-color: gradientLight3; }
.paginationButton:disabled { color: gradientDark4; }
.paginationButton:disabled:hover { background-color: white; }

/* --- Container Styles --- */
.container .controlsContainer { padding: 12px 10px; }
</style>
